import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Target, TrendingDown, Users, FileSearch, CreditCard, Loader2, RefreshCw,
} from "lucide-react";

const WINDOWS = [
  { value: 7,   label: "7d" },
  { value: 30,  label: "30d" },
  { value: 90,  label: "90d" },
  { value: 365, label: "All" },
];

const STAGE_ICONS = {
  signup:  Users,
  audited: FileSearch,
  paid:    CreditCard,
};

const STAGE_TONE = {
  signup:  "bg-foreground text-background",
  audited: "bg-amber-500 text-white",
  paid:    "bg-emerald-500 text-white",
};

/**
 * Admin-only Conversion Radar — funnel viz for SMB onboarding.
 *
 * Shows three stacked horizontal bars (Signed up → Ran first audit →
 * Upgraded to paid) with absolute counts, percentages of the top stage,
 * Quick-Start vs Standard signup split, and drop-off rates between stages.
 *
 * Hidden entirely for non-admins.
 */
export default function ConversionRadar({ user }) {
  const isAdmin = user && typeof user === "object" && user.role === "admin";
  const [window, setWindow] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = async (w = window) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/funnel-stats?window=${w}`);
      setData(data);
    } catch (e) {
      setData({ _error: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) refresh(window);
  }, [isAdmin, window]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin) return null;
  if (data === null && !loading) return null;

  const stages = data?.stages || [];
  const top = stages[0]?.count || 0;

  return (
    <div className="mb-8 border border-foreground bg-white" data-testid="conversion-radar">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 bg-foreground grid place-items-center text-background flex-shrink-0">
            <Target className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              // conversion radar · admin only
            </div>
            <div className="text-sm font-medium mt-0.5">
              {data?._error ? "Couldn't load funnel" :
                top === 0
                  ? "No signups in this window yet"
                  : `${top} signup${top === 1 ? "" : "s"} → ${data?.conversion_rate_pct ?? 0}% upgrade rate`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Window picker */}
          <div className="border border-border flex" data-testid="conversion-radar-window-picker">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => setWindow(w.value)}
                disabled={loading}
                data-testid={`conversion-radar-window-${w.value}`}
                className={
                  "px-2.5 py-1 text-xs font-mono-data uppercase tracking-widest border-r border-border last:border-r-0 transition-colors " +
                  (window === w.value
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground")
                }
              >
                {w.label}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-none"
            onClick={() => refresh()}
            disabled={loading}
            data-testid="conversion-radar-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {data?._error ? (
        <div className="p-4 text-sm text-red-600" data-testid="conversion-radar-error">{data._error}</div>
      ) : top === 0 ? (
        <div className="p-6 text-sm text-muted-foreground" data-testid="conversion-radar-empty">
          No signups in this window. Switch to a wider window above, or wait for traffic.
        </div>
      ) : (
        <div className="p-5" data-testid="conversion-radar-funnel">
          <ol className="space-y-3">
            {stages.map((stage) => {
              const Icon = STAGE_ICONS[stage.key] || Users;
              const widthPct = Math.max(2, stage.pct_of_top || 0); // never invisibly small
              return (
                <li key={stage.key} data-testid={`conversion-radar-stage-${stage.key}`}>
                  <div className="flex items-center gap-3 mb-1.5">
                    <Icon className="w-4 h-4 text-foreground" />
                    <span className="font-semibold tracking-tight text-sm">{stage.label}</span>
                    <span className="font-mono-data text-xs text-muted-foreground ml-auto">
                      {stage.count.toLocaleString()} · {stage.pct_of_top}%
                    </span>
                  </div>
                  {/* Bar */}
                  <div className="h-7 bg-secondary/30 relative overflow-hidden">
                    <div
                      className={`h-full ${STAGE_TONE[stage.key] || "bg-foreground"} transition-all`}
                      style={{ width: `${widthPct}%` }}
                    />
                    {/* Breakdown overlay (e.g. Quick Start vs Standard on the signup stage) */}
                    {stage.breakdown && stage.breakdown.length > 0 && (
                      <div className="absolute inset-0 flex items-center px-3 pointer-events-none">
                        <div className="flex items-center gap-3 text-[10px] font-mono-data uppercase tracking-widest text-background/85">
                          {stage.breakdown.map((b) => (
                            <span key={b.label}>
                              {b.label}: {b.count} ({b.pct_of_stage}%)
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {/* Drop-offs */}
          {(data?.drop_offs || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-border" data-testid="conversion-radar-dropoffs">
              <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                // drop-offs between stages
              </div>
              <ul className="grid sm:grid-cols-2 gap-3">
                {data.drop_offs.map((d, idx) => (
                  <li
                    key={`${d.from}-${d.to}`}
                    className={
                      "border p-3 flex items-center gap-3 " +
                      (d.lost_pct >= 70
                        ? "border-red-300 bg-red-50"
                        : d.lost_pct >= 40
                          ? "border-amber-300 bg-amber-50"
                          : "border-border bg-secondary/30")
                    }
                    data-testid={`conversion-radar-dropoff-${idx}`}
                  >
                    <TrendingDown className={
                      "w-4 h-4 flex-shrink-0 " +
                      (d.lost_pct >= 70 ? "text-red-600" : d.lost_pct >= 40 ? "text-amber-700" : "text-muted-foreground")
                    } />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                        {d.from} → {d.to}
                      </div>
                      <div className="text-sm font-medium leading-tight">
                        {d.lost.toLocaleString()} lost · {d.lost_pct}% drop
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
